
import React from 'react';
import { Button } from '@/components/ui/button';
import { Eye, EditIcon, CheckCircle, SendIcon, Trash2 } from 'lucide-react';
import { Interview } from '@/types/interview';
import { useTranslation } from 'react-i18next';

interface TableActionButtonsProps {
  interview: Interview;
  onViewInterview: (interview: Interview) => void;
  onEditInterview: (interview: Interview) => void;
  onPublish: (interviewId: string) => void;
  onSendToFeeder: (interviewId: string) => void;
  onDelete: (interviewId: string) => void;
  publishingId: string | null;
  sendingToFeederId: string | null;
  deletingId: string | null;
}

const TableActionButtons: React.FC<TableActionButtonsProps> = ({
  interview,
  onViewInterview,
  onEditInterview,
  onPublish,
  onSendToFeeder,
  onDelete,
  publishingId,
  sendingToFeederId,
  deletingId
}) => {
  const { t } = useTranslation();
  const isProcessing = publishingId !== null || sendingToFeederId !== null || deletingId !== null;
  const isCurrentProcessing = publishingId === interview.id || sendingToFeederId === interview.id || deletingId === interview.id;
  
  return (
    <div className="flex space-x-2">
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => onViewInterview(interview)}
        disabled={isProcessing}
      >
        <Eye className="h-4 w-4 mr-1" />
        {t('interviewManagement.view')}
      </Button>
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => onEditInterview(interview)}
        disabled={isProcessing}
      >
        <EditIcon className="h-4 w-4 mr-1" />
        {t('interviewManagement.edit')}
      </Button>
      <Button 
        variant="outline" 
        size="sm"
        className={`${publishingId === interview.id ? 'bg-green-50' : ''} text-green-600 hover:text-green-700 hover:bg-green-50`}
        onClick={() => onPublish(interview.id)}
        disabled={isProcessing}
      >
        <CheckCircle className="h-4 w-4 mr-1" />
        {publishingId === interview.id ? t('interviewManagement.publishing') : t('interviewManagement.publish')}
      </Button>
      
      {/* Only show Send til køen button if interview is not already in feeder */}
      {!interview.is_in_feeder && (
        <Button 
          variant="outline" 
          size="sm"
          className={`${sendingToFeederId === interview.id ? 'bg-blue-50' : ''} text-blue-600 hover:text-blue-700 hover:bg-blue-50`}
          onClick={() => onSendToFeeder(interview.id)}
          disabled={isProcessing}
        >
          <SendIcon className="h-4 w-4 mr-1" />
          {sendingToFeederId === interview.id ? t('interviewManagement.sending') : t('interviewManagement.sendToQueue')}
        </Button>
      )}
      
      {/* Delete button */}
      <Button 
        variant="outline" 
        size="sm"
        className={`${deletingId === interview.id ? 'bg-red-50' : ''} text-red-600 hover:text-red-700 hover:bg-red-50`}
        onClick={() => onDelete(interview.id)}
        disabled={isProcessing}
      >
        <Trash2 className="h-4 w-4 mr-1" />
        {deletingId === interview.id ? t('interviewManagement.deleting') : t('interviewManagement.delete')}
      </Button>
    </div>
  );
};

export default TableActionButtons;
